import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, RadioCardGroup } from '@knack/asterisk-react';

import { cn } from '@/utils/tailwind';
import { type QuestionProps, type RadioSelected } from './types';
import { getOnboardingTranslateKey } from './utils';

function RadioCardItem({ option, disabled, questionId, hasInput, onInputTextChange, inputValue }) {
  const [t] = useTranslation();

  return (
    <div className="flex w-full flex-col gap-4">
      <RadioCardGroup.Card
        key={option}
        className={cn('h-fit items-center', disabled && 'opacity-70')}
        value={option}
        width="100%"
        data-disabled={disabled}
        data-testid={`onboarding-question-radio-boxes-option-${option}`}
      >
        <RadioCardGroup.Content
          title={t(getOnboardingTranslateKey(questionId, 'options', option))}
        />
      </RadioCardGroup.Card>
      {hasInput && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="other-input" className="text-sm">
            {t(getOnboardingTranslateKey(questionId, 'other'))}
          </Label>

          <Input
            title="Other"
            id="other-input"
            type="text"
            data-testid="onboarding-question-radio-boxes-other-input"
            disabled={disabled}
            onChange={onInputTextChange}
            value={inputValue}
          />
        </div>
      )}
    </div>
  );
}

export function QuestionRadioBoxes({
  questionId,
  questionOptions,
  questionLayout,
  disabled,
  selected,
  onChangeSelection
}: QuestionProps<RadioSelected>) {
  const [t] = useTranslation();
  const [otherSelected, setOtherSelected] = useState(selected?.option === 'other');

  const onValueChange = (option: string) => {
    setOtherSelected(option === 'other');
    onChangeSelection({
      option
    });
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeSelection({
      option: 'other',
      other: e.target.value
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">{t(getOnboardingTranslateKey(questionId, 'title'))}</p>

      <RadioCardGroup
        defaultValue={selected?.option as string}
        disabled={disabled}
        className={cn('gap-4', questionLayout.className)}
        onValueChange={onValueChange}
      >
        {questionLayout.grid
          ? questionLayout.grid.map((col, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="flex flex-1 flex-col gap-4">
                {col.map((option) => {
                  const optionKey = typeof option === 'number' ? questionOptions[option] : option;

                  return (
                    <RadioCardItem
                      key={optionKey}
                      option={optionKey}
                      disabled={disabled}
                      questionId={questionId}
                      hasInput={otherSelected && optionKey === 'other'}
                      onInputTextChange={onTextChange}
                      inputValue={selected?.other}
                    />
                  );
                })}
              </div>
            ))
          : questionOptions.map((option) => (
              <RadioCardItem
                key={option}
                option={option}
                disabled={disabled}
                questionId={questionId}
                hasInput={otherSelected && option === 'other'}
                onInputTextChange={onTextChange}
                inputValue={selected?.other}
              />
            ))}
      </RadioCardGroup>
    </div>
  );
}
